const SECTION_ID = "waitlist-form";
const MODAL_ID = "waitlist-modal";
const KLAVIYO_BACK_IN_STOCK_URL =
  "https://a.klaviyo.com/client/back-in-stock-subscriptions/";
const KLAVIYO_API_REVISION = "2026-01-15";

const requests = new Map();

function loadWaitlistForm(url) {
  if (requests.has(url)) return requests.get(url);

  const sectionUrl = new URL(url, window.location.origin);
  sectionUrl.searchParams.set("section_id", SECTION_ID);

  const request = fetch(sectionUrl).then((response) => {
    if (!response.ok) throw new Error(`Waitlist failed: ${response.status}`);
    return response.text();
  });

  // Let a failed request be retried rather than cached.
  request.catch(() => requests.delete(url));
  requests.set(url, request);

  return request;
}

/**
 * Opens the waitlist modal on a product's sign-up form. Stacks over any
 * modal already open, such as quick add.
 *
 * @param {HTMLElement} trigger Refocused when the modal closes
 * @param {string} url Product URL
 * @param {number|string} [variantId] Preselected in the form
 */
export function openWaitlist(trigger, url, variantId) {
  const html = loadWaitlistForm(url).then((markup) => {
    const form = new DOMParser()
      .parseFromString(markup, "text/html")
      .querySelector("waitlist-form");

    if (!form) throw new Error(`No waitlist markup for ${url}`);

    // Set as an attribute, so it survives the trip through innerHTML.
    const option = form.querySelector(`option[value="${variantId}"]`);
    if (option) {
      form
        .querySelectorAll("option[selected]")
        .forEach((selected) => selected.removeAttribute("selected"));
      option.setAttribute("selected", "");
    }

    return form.outerHTML;
  });

  html.catch(() =>
    document.dispatchEvent(
      new CustomEvent("markup-modal:close", { detail: { target: MODAL_ID } }),
    ),
  );

  trigger.dispatchEvent(
    new CustomEvent("markup-modal:open", {
      bubbles: true,
      detail: { target: MODAL_ID, html },
    }),
  );
}

// For page bundles, which would otherwise import a second copy with a
// separate cache.
window.openWaitlist = openWaitlist;

/**
 * Subscribes an email to Klaviyo's back-in-stock alert for the chosen
 * variant.
 */
class WaitlistForm extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector("form");
    this.submit = this.querySelector("[data-waitlist-submit]");
    this.error = this.querySelector("[data-waitlist-error]");
    this.success = this.querySelector("[data-waitlist-success]");

    this.form.addEventListener("submit", this._onSubmit);
  }

  disconnectedCallback() {
    this.form.removeEventListener("submit", this._onSubmit);
  }

  _onSubmit = async (event) => {
    event.preventDefault();

    const { companyId, errorInvalid, errorGeneric } = this.dataset;
    const { email, variant } = this.form.elements;

    if (!email.value.trim() || !email.checkValidity()) {
      this._showError(errorInvalid, { invalid: true });
      return;
    }

    if (!companyId) {
      this._showError(errorGeneric);
      return;
    }

    this._showError("");
    this.submit.disabled = true;

    try {
      const response = await fetch(
        `${KLAVIYO_BACK_IN_STOCK_URL}?company_id=${encodeURIComponent(companyId)}`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/vnd.api+json",
            revision: KLAVIYO_API_REVISION,
          },
          body: JSON.stringify({
            data: {
              type: "back-in-stock-subscription",
              attributes: {
                channels: ["EMAIL"],
                profile: {
                  data: {
                    type: "profile",
                    attributes: { email: email.value.trim() },
                  },
                },
              },
              relationships: {
                variant: {
                  data: {
                    type: "catalog-variant",
                    id: `$shopify:::$default:::${variant.value}`,
                  },
                },
              },
            },
          }),
        },
      );

      if (!response.ok) throw new Error(response.status);

      this.submit.hidden = true;
      this.success.hidden = false;
      // The focused submit button has just been hidden.
      this.success.focus();
    } catch {
      this.submit.disabled = false;
      this._showError(errorGeneric);
    }
  };

  _showError(message, { invalid = false } = {}) {
    const { email } = this.form.elements;

    this.error.textContent = message;
    email.classList.toggle("error", invalid);

    if (invalid) {
      email.setAttribute("aria-invalid", "true");
    } else {
      email.removeAttribute("aria-invalid");
    }
  }
}

if (!customElements.get("waitlist-form")) {
  customElements.define("waitlist-form", WaitlistForm);
}
